import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import CrudDialog from "../components/CrudDialog";
import { toast } from "sonner";
import { Fingerprint, Upload, Trash2, Copy, Plus, Pencil, Cpu, ScanSearch, KeyRound } from "lucide-react";
import { BUILTIN_MODELS, extractImmo, toInt } from "../lib/immoDecode";

const BRANDS = ["Honda", "Yamaha", "Suzuki", "Kawasaki"];

const FIELDS = [
  { key: "name", label: "Model Name", placeholder: "Honda Vario / Click 30400-K2SA" },
  { key: "brand", label: "Brand", type: "select", options: BRANDS },
  { key: "eeprom", label: "EEPROM / IC", placeholder: "93C46 · 512B" },
  { key: "file_size", label: "File Size (bytes, optional)", placeholder: "512" },
  { key: "offset", label: "Offset (decimal or 0x hex)", placeholder: "60 or 0x3C" },
  { key: "offsetB", label: "Offset B (xadv14 high-half, optional)", placeholder: "0x46" },
  { key: "length", label: "Length (bytes)", placeholder: "4" },
  { key: "endian", label: "Byte Order", type: "select", options: ["little", "big"] },
  { key: "format", label: "Format", type: "select", options: ["decimal", "ascii", "bcd", "hex", "xadv14"] },
  { key: "pad", label: "Zero-pad Digits (optional)", placeholder: "9" },
  { key: "notes", label: "Notes", type: "textarea", placeholder: "Where/how the IMMO ID is stored" },
];

export default function ImmoCalculator() {
  return <ImmoCalculatorInner />;
}

function ImmoCalculatorInner() {
  const { isAdmin } = useAuth();
  const inputRef = useRef(null);
  const [models, setModels] = useState([]);
  const [buf, setBuf] = useState(null);
  const [fileName, setFileName] = useState("");
  const [dragging, setDragging] = useState(false);
  const [modelId, setModelId] = useState("auto"); // 'auto' or a model id
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = async () => { const r = await api.get("/immo-models"); setModels(r.data); };
  useEffect(() => { load(); }, []);

  const allModels = useMemo(() => [...BUILTIN_MODELS, ...models], [models]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".bin")) { toast.error("Please select a .bin EEPROM dump"); return; }
    const ab = await file.arrayBuffer();
    setBuf(ab); setFileName(file.name);
    toast.success(`Loaded ${file.name} — ${ab.byteLength} bytes`);
  };

  // Auto search: run every model; prefer file-size matches and plausible (non-zero) IDs.
  const plausible = (s) => !!s && s.length >= 6 && /[1-9]/.test(s);
  const score = (r) => (r.sizeMatch ? 2 : 0) + (plausible(r.immo) ? 1 : 0);
  const autoResults = useMemo(() => {
    if (!buf) return [];
    return allModels
      .map((m) => ({ model: m, immo: extractImmo(buf, m), sizeMatch: toInt(m.file_size) === buf.byteLength }))
      .filter((r) => r.immo)
      .sort((a, b) => score(b) - score(a));
  }, [buf, allModels]);

  const selectedModel = allModels.find((m) => m.id === modelId);
  const primary = useMemo(() => {
    if (!buf) return null;
    if (modelId === "auto") return autoResults[0] || null;
    return selectedModel ? { model: selectedModel, immo: extractImmo(buf, selectedModel), sizeMatch: toInt(selectedModel.file_size) === buf.byteLength } : null;
  }, [buf, modelId, autoResults, selectedModel]);

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("IMMO ID copied"); };

  const submit = async (form) => {
    try {
      if (dialog.initial) await api.put(`/immo-models/${dialog.initial.id}`, form);
      else await api.post("/immo-models", form);
      toast.success("Model saved");
      setDialog({ open: false, initial: null });
      load();
    } catch { toast.error("Save failed — admin only"); }
  };
  const del = async (id) => {
    try { await api.delete(`/immo-models/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Fingerprint className="h-4 w-4" /> IMMO Tool</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">IMMO Calculator</h1>
        <p className="text-slate-500 text-sm mt-1">Drop an EEPROM dump and read the <span className="text-cyan-300 font-mono-tt">IMMO ID</span> automatically. Everything runs in your browser.</p>
      </div>

      {/* Dropzone */}
      <div
        data-testid="immo-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-cyan-400 bg-cyan-400/5" : "border-slate-700 bg-[#0c1017]"}`}
      >
        <input ref={inputRef} data-testid="immo-file-input" type="file" accept=".bin" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        <Upload className="h-8 w-8 text-slate-500 mx-auto mb-3" />
        <p className="text-slate-300 font-semibold">Drop your <span className="font-mono-tt text-cyan-300">.bin</span> EEPROM dump here</p>
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <button data-testid="immo-browse-btn" onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold transition-colors">
            <Upload className="h-4 w-4" /> Browse .bin
          </button>
          {buf && (
            <button data-testid="immo-clear-btn" onClick={() => { setBuf(null); setFileName(""); if (inputRef.current) inputRef.current.value = ""; }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold">
              <Trash2 className="h-4 w-4" /> Clear
            </button>
          )}
        </div>
        {fileName && <p className="mt-3 text-xs font-mono-tt text-slate-500">{fileName} · {buf?.byteLength} bytes</p>}
      </div>

      {buf && (
        <div className="grid lg:grid-cols-3 gap-5 mt-6">
          <div className="lg:col-span-2 space-y-5">
            {/* Result */}
            <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2"><KeyRound className="h-5 w-5 text-cyan-400" /> IMMO Result</h2>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">Model</span>
                  <select data-testid="immo-model-select" value={modelId} onChange={(e) => setModelId(e.target.value)}
                    className="appearance-none bg-[#121824] border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400">
                    <option value="auto">⚡ Auto search</option>
                    {allModels.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
                  </select>
                </div>
              </div>

              {primary && primary.immo ? (
                <div className="rounded-lg border border-red-700/50 bg-red-950/20 p-6 text-center">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300/80">IMMO ID</div>
                  <div data-testid="immo-id-value" className="font-head font-black text-4xl sm:text-5xl text-white tracking-[0.15em] mt-2 break-all">{primary.immo}</div>
                  <div className="text-xs font-mono-tt text-slate-500 mt-2">{primary.model.name} · {primary.model.eeprom || ""} {primary.sizeMatch ? "· size ✓" : ""}</div>
                  <button data-testid="immo-copy-btn" onClick={() => copy(primary.immo)}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold text-sm">
                    <Copy className="h-4 w-4" /> Copy ID
                  </button>
                  {primary.model.format === "canbus24" && (
                    <div data-testid="immo-canbus24-note" className="mt-4 text-left rounded-lg border border-amber-700/50 bg-amber-950/20 p-3 text-xs text-amber-200/90 font-mono-tt">
                      ⚠ ECU dump = LOW 24-bit only (0x{((new Uint8Array(buf)[0x0f]<<16)|(new Uint8Array(buf)[0x0e]<<8)|new Uint8Array(buf)[0x11]).toString(16).toUpperCase().padStart(6,"0")}).
                      The leading digit(s) are stored ONLY in the <b>SCU EEPROM</b>. Load the matching SCU dump to read the full IMMO ID — the ECU's low 24 bits will match the SCU's.
                    </div>
                  )}
                </div>
              ) : (
                <div data-testid="immo-no-result" className="rounded-lg border border-slate-800 bg-[#07090e] p-6 text-center text-slate-500 font-mono-tt text-sm">
                  No IMMO ID could be read. Pick the correct model or add a definition.
                </div>
              )}
            </div>

            {/* Auto-search candidates */}
            {modelId === "auto" && (
              <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5">
                <h2 className="font-head font-bold text-lg uppercase text-white flex items-center gap-2 mb-4"><ScanSearch className="h-5 w-5 text-cyan-400" /> Auto-Search Candidates</h2>
                <div className="overflow-x-auto">
                  <table data-testid="immo-candidates-table" className="w-full text-sm">
                    <thead><tr className="bg-[#121824] border-b border-slate-800">
                      <th className="text-left px-3 py-2.5 font-mono-tt text-[11px] uppercase tracking-wider text-cyan-400/90">Model</th>
                      <th className="text-left px-3 py-2.5 font-mono-tt text-[11px] uppercase tracking-wider text-cyan-400/90">IMMO ID</th>
                      <th className="text-left px-3 py-2.5 font-mono-tt text-[11px] uppercase tracking-wider text-cyan-400/90">Match</th>
                    </tr></thead>
                    <tbody>
                      {autoResults.map((r) => (
                        <tr key={r.model.id} className="border-b border-slate-800/60 hover:bg-cyan-400/5">
                          <td className="px-3 py-2.5 text-slate-200">{r.model.name}</td>
                          <td className="px-3 py-2.5 font-mono-tt text-white font-bold">{r.immo}</td>
                          <td className="px-3 py-2.5 font-mono-tt text-xs">{r.sizeMatch ? <span className="text-emerald-400">size ✓</span> : <span className="text-slate-500">—</span>}</td>
                        </tr>
                      ))}
                      {autoResults.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-600 font-mono-tt">No candidates.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Model detail */}
          <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5 self-start">
            <h2 className="font-head font-bold text-lg uppercase text-white flex items-center gap-2 mb-4"><Cpu className="h-5 w-5 text-cyan-400" /> Definition</h2>
            {primary?.model ? (
              <dl className="space-y-2.5 text-sm font-mono-tt">
                {[["Model", primary.model.name], ["Brand", primary.model.brand], ["EEPROM", primary.model.eeprom],
                  ["Offset", primary.model.offset], ["Offset B", primary.model.offsetB], ["Length", primary.model.length], ["Endian", primary.model.endian],
                  ["Format", primary.model.format], ["Pad", primary.model.pad], ["File size", primary.model.file_size]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-slate-800/60 pb-1.5">
                    <dt className="text-[10px] uppercase tracking-widest text-slate-500">{k}</dt>
                    <dd className="text-slate-200 text-right break-all">{v || "—"}</dd>
                  </div>
                ))}
                {primary.model.notes && <p className="text-xs text-slate-500 pt-1">{primary.model.notes}</p>}
              </dl>
            ) : <p className="text-slate-600 text-sm font-mono-tt">Select a model to see its definition.</p>}
          </div>
        </div>
      )}

      {/* Model database (admin) */}
      <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2"><Fingerprint className="h-5 w-5 text-cyan-400" /> IMMO Model Database</h2>
          {isAdmin && (
            <Button data-testid="immo-add-btn" onClick={() => setDialog({ open: true, initial: null })}
              className="bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
              <Plus className="h-4 w-4 mr-1.5" /> Add Model
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table data-testid="immo-models-table" className="w-full text-sm">
            <thead><tr className="bg-[#121824] border-b border-slate-800">
              {["Model", "Brand", "EEPROM", "Offset", "Len", "Endian", "Format", "Pad"].map((h) => (
                <th key={h} className="text-left px-3 py-2.5 font-mono-tt text-[11px] uppercase tracking-wider text-cyan-400/90 whitespace-nowrap">{h}</th>
              ))}
              {isAdmin && <th className="px-3 py-2.5 text-right text-[11px] uppercase text-slate-500">Actions</th>}
            </tr></thead>
            <tbody>
              {allModels.map((m) => (
                <tr key={m.id} className="border-b border-slate-800/60 hover:bg-cyan-400/5">
                  <td className="px-3 py-2.5 font-semibold text-white whitespace-nowrap">{m.name} {m.builtin && <span className="ml-1 text-[9px] font-mono-tt uppercase tracking-wider text-emerald-400 border border-emerald-800/50 rounded px-1 py-0.5 align-middle">built-in</span>}</td>
                  <td className="px-3 py-2.5 text-slate-300">{m.brand}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-300 whitespace-nowrap">{m.eeprom || "—"}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-300">{m.offset}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-300">{m.length}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-300">{m.endian}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-300">{m.format}</td>
                  <td className="px-3 py-2.5 font-mono-tt text-slate-300">{m.pad || "—"}</td>
                  {isAdmin && (
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {m.builtin ? <span className="text-[10px] text-slate-600 font-mono-tt">locked</span> : (<>
                        <button data-testid={`immo-edit-btn-${m.id}`} onClick={() => setDialog({ open: true, initial: m })} className="text-slate-400 hover:text-cyan-300 p-1"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`immo-delete-btn-${m.id}`} onClick={() => del(m.id)} className="text-slate-400 hover:text-red-400 p-1 ml-1"><Trash2 className="h-4 w-4" /></button>
                      </>)}
                    </td>
                  )}
                </tr>
              ))}
              {allModels.length === 0 && <tr><td colSpan={isAdmin ? 9 : 8} className="px-3 py-8 text-center text-slate-600 font-mono-tt">No models yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <CrudDialog open={dialog.open} onOpenChange={(o) => setDialog({ open: o, initial: o ? dialog.initial : null })}
        title={dialog.initial ? "Edit IMMO Model" : "Add IMMO Model"} fields={FIELDS} initial={dialog.initial}
        onSubmit={submit} testidPrefix="immo" />
    </div>
  );
}
